/**
 * 메시지 화면의 순수 로직.
 * 렌더링과 분리해 두어야 양방향 동작을 테스트로 검증할 수 있다.
 */

export const fmtTime = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h >= 12 ? '오후' : '오전'} ${h % 12 === 0 ? 12 : h % 12}:${m}`;
};

export const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * 오래된 것이 위, 최신이 아래. 일반 메신저와 같은 순서.
 * 커서 페이징(before=id) 특성상 서버가 내림차순으로 줄 수 있어
 * 표시 직전에 항상 오름차순으로 맞춘다.
 */
export function sortMessagesAsc(messages) {
  return [...(messages ?? [])].sort((a, b) => {
    const ta = new Date(a?.createdAt ?? 0).getTime();
    const tb = new Date(b?.createdAt ?? 0).getTime();
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
    // 같은 시각이거나 시간이 없으면 id 순 (id는 증가한다고 가정)
    return (Number(a?.id) || 0) - (Number(b?.id) || 0);
  });
}

/** 서버 메시지 배열에 날짜 구분선을 끼워 넣는다 */
export function withDateDividers(messages) {
  const out = [];
  let lastDate = null;
  for (const m of sortMessagesAsc(messages)) {
    const date = fmtDate(m.createdAt);
    if (date && date !== lastDate) {
      out.push({ id: `date-${date}`, type: 'date', text: date });
      lastDate = date;
    }
    out.push({
      id: m.id,
      text: m.body,
      isMine: Boolean(m.isMine),
      time: fmtTime(m.createdAt),
      status: m.status,
    });
  }
  return out;
}

export const SEND_DENY = {
  BLOCKED: 'BLOCKED',
  I_BLOCKED: 'I_BLOCKED',
  RECEIVER_CLOSED: 'RECEIVER_CLOSED',
};

/**
 * 발신 가능 여부 — 버튼을 미리 막기 위한 UI 힌트.
 * 최종 판정은 서버가 하며, 규칙은 서버와 동일하게 유지해야 한다.
 *
 * 순서가 중요하다: '대화가 이미 있으면 허용'이 수신 허용 토글보다 앞선다.
 * 제작사가 먼저 연락한 경우도 대화가 생성되므로 같은 규칙으로 처리된다.
 */
export function evaluateSendPermission({
  conversationExists = false,
  peerBlockedMe = false,
  iBlockedPeer = false,
  targetIsProducer = false,
  targetAllowsIncoming = true,
} = {}) {
  if (peerBlockedMe) return { allowed: false, reason: SEND_DENY.BLOCKED };
  if (iBlockedPeer) return { allowed: false, reason: SEND_DENY.I_BLOCKED };
  if (conversationExists) return { allowed: true };
  if (targetIsProducer && !targetAllowsIncoming) {
    return { allowed: false, reason: SEND_DENY.RECEIVER_CLOSED };
  }
  return { allowed: true };
}

export const SEND_DENY_MESSAGE = {
  [SEND_DENY.BLOCKED]: '상대가 대화를 차단해 메시지를 보낼 수 없습니다.',
  [SEND_DENY.I_BLOCKED]: '차단한 대화입니다. 차단을 해제하면 다시 보낼 수 있습니다.',
  [SEND_DENY.RECEIVER_CLOSED]: '이 제작사는 새 메시지 수신을 받지 않고 있습니다.',
};

/**
 * 목록에 쓰는 상대 시간. 백엔드가 "10분전" 대신 ISO(LocalDateTime)를 주므로
 * 표시 문자열은 프론트에서 만든다.
 */
export function fmtRelative(value, now = Date.now()) {
  if (value == null || value === '') return '';
  // 이미 표시용 문자열이면 그대로 둔다 (백엔드가 바뀌어도 깨지지 않게)
  if (typeof value === 'string' && !/^\d{4}-\d{2}-\d{2}/.test(value)) return value;

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const diffSec = Math.floor((now - d.getTime()) / 1000);
  if (diffSec < 0) return '방금';
  if (diffSec < 60) return '방금';
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}분전`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour}시간전`;
  const day = Math.floor(hour / 24);
  if (day < 7) return `${day}일전`;
  return fmtDate(d.toISOString());
}

/**
 * 서버 대화 객체를 화면이 쓰는 모양으로 맞춘다.
 * 필드명이 조금 달라도 견디도록 후보를 순회하고, 시간은 표시용으로 변환한다.
 */
export function normalizeConversation(raw, now = Date.now()) {
  if (!raw) return null;
  return {
    ...raw,
    id: raw.id,
    company: raw.company ?? raw.peerName ?? raw.displayName ?? raw.name ?? '',
    project: raw.project ?? raw.subtitle ?? raw.peerSubtitle ?? '',
    preview: raw.preview ?? raw.lastMessage ?? raw.lastMessageBody ?? '',
    time: fmtRelative(raw.time ?? raw.lastMessageAt ?? raw.updatedAt, now),
    unread: Number(raw.unread ?? raw.unreadCount ?? 0) || 0,
    blocked: Boolean(raw.blocked),
    peerBlockedMe: Boolean(raw.peerBlockedMe),
    avatarUrl: raw.avatarUrl ?? raw.profileImageUrl ?? null,
    posting: raw.posting ?? raw.castingPost ?? null,
  };
}
